import React, { useCallback, useMemo, useState } from "react";

import { formatCallingFrom, maskPhone } from "../helper/patientFormatters";
import PatientCommunicationService from "../service/PatientCommunicationService";
import AddressBookDialog from "./AddressBookDialog";
import CallInitiatedDialog from "./CallInitiatedDialog";
import CallErrorDialog from "./CallErrorDialog";
import { useActiveCall } from "./ActiveCallProvider";

/**
 * Calling the patient — ONE implementation for every screen that offers it.
 *
 * ARCHITECTURE (post-2026-09-27, ZY-10202): the AiTranscript modal + the
 * LiveKit call are owned by ActiveCallProvider at app root, not by this
 * hook. useCallFlow is now a thin per-screen adapter: it fires
 * POST /api/v1/patient-communication/initiate-call, branches on
 * `response.vendor`, and either hands off to the provider (aspire) or
 * shows a per-screen "Call initiated" dialog (sparktg).
 *
 *   • "aspire"  → provider.startAspireCall({patient, appointment,
 *                 seededSession}). Provider mounts the modal (already
 *                 alive across route changes) and drives it straight
 *                 into the ringing state via the LiveKit seed.
 *   • "sparktg" → local CallInitiatedDialog. No modal.
 *
 * The response ALWAYS carries `callerCountryCode` + `callerPhoneNo`, the
 * mapped number the patient will see. Surfaced as "Calling from: +91
 * XXXXX X3285" on the dialogs and inside the transcript modal.
 *
 * `appointment` is REQUIRED — the backend rejects without an
 * appointmentId. The address-book dialog and the confirm/error dialogs
 * are STILL rendered by each caller (they're per-screen, transient), so
 * this hook keeps its own `dialogsUI` / `addressBookUI` returns — only
 * `transcriptUI` is gone.
 *
 * `callActive` comes straight from the provider, so every Call button
 * everywhere (header, address book, sidebar row) disables consistently
 * while a call is up — a second /initiate-call while one call is live
 * would just be blocked by the button anyway, and reopen-via-ECHO-row
 * plus the floater are the intended paths back into the live call.
 */
const useCallFlow = ({ patient, appointment, callingNumber }) => {
  const activeCall = useActiveCall();

  const [addressBookOpen, setAddressBookOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null); // {callingFrom}
  const [errorDialog, setErrorDialog] = useState(null);      // {title, message}

  const service = React.useRef(new PatientCommunicationService()).current;

  /* Resolve the primary number source once — declared BEFORE the
     callbacks below so their closures don't depend on it being defined
     later in the render body. `callingNumber === undefined` means "use
     the patient's primary"; explicit null means "no number available"
     (booking-for path where the mapping hasn't resolved yet). */
  const numberSource = callingNumber === undefined ? patient : callingNumber;
  const primaryCc = numberSource && numberSource.countryCode;
  const primaryPhone = numberSource && numberSource.phoneno;

  /* Shape the patient record the modal + provider identify the call
     with. Same shape the aspire hand-off and the sidebar-row openCall
     both need, so hoist it into a memo. */
  const shapedPatient = useMemo(() => {
    if (!patient) return null;
    return {
      ...patient,
      id: patient.id,
      name: `${patient.firstName} ${patient.lastName || ""}`.trim(),
      phone: maskPhone(primaryCc, primaryPhone),
      pid: `PID ${patient.id}`,
      countryCode: primaryCc || "",
      phoneno: primaryPhone || "",
    };
  }, [patient, primaryCc, primaryPhone]);

  /** Dial a specific number: fire /initiate-call and branch on vendor. */
  const callNumber = useCallback((target) => {
    if (!patient) return;
    /* Defense in depth — every UI entry point already disables while
       callActive. If something bypasses (keyboard shortcut, stale
       cached button), restore the existing call rather than start a
       second one. */
    if (activeCall.callActive) {
      activeCall.restore();
      return;
    }
    const appointmentId = appointment && appointment.id;
    if (!appointmentId) {
      setErrorDialog({
        title: "Appointment required",
        message: "An appointment must be selected to call the patient.",
      });
      return;
    }
    const patientCC = (target && target.countryCode) || "";
    const patientPh = (target && target.phoneNo) || "";
    if (!patientPh) {
      setErrorDialog({
        title: "Phone number missing",
        message: "The patient has no phone number on file to call.",
      });
      return;
    }

    service.initiateCall(
      {
        patientId:          patient.id,
        patientCountryCode: String(patientCC),
        patientPhoneNo:     String(patientPh),
        appointmentId:      Number(appointmentId),
      },
      (res) => {
        const vendor = res && res.vendor;
        const callingFrom = formatCallingFrom(
          res && res.callerCountryCode,
          res && res.callerPhoneNo,
        );
        if (vendor === "aspire") {
          const aspire = res.aspire || {};
          activeCall.startAspireCall({
            patient: shapedPatient,
            appointment,
            seededSession: {
              transcriptId:   aspire.transcriptId,
              callSid:        aspire.callSid,
              callingFrom,
              mode:           aspire.mode,
              livekitUrl:     aspire.livekitUrl,
              livekitRoom:    aspire.livekitRoom,
              livekitToken:   aspire.livekitToken,
              tokenExpiresAt: aspire.tokenExpiresAt,
            },
          });
          return;
        }
        /* SparkTG path — no modal, just the confirm dialog. */
        setConfirmDialog({ callingFrom });
      },
      (err) => {
        const msg = (err && err.message) || "Unable to initiate the call. Please try again.";
        setErrorDialog({ title: "Call failed", message: msg });
      },
    );
  }, [patient, appointment, service, activeCall, shapedPatient]);

  /** Sidebar's "ECHO - AI transcript" row. If a call is already up,
   *  restore it (matches the floater's own click behaviour). Otherwise
   *  open the modal on S0 with the current patient / appointment so the
   *  CM can pick a number and place a call. */
  const openCall = useCallback(() => {
    if (activeCall.callActive) {
      activeCall.restore();
      return;
    }
    activeCall.openCall(shapedPatient, appointment);
  }, [activeCall, shapedPatient, appointment]);

  const openAddressBook = useCallback(() => setAddressBookOpen(true), []);

  const hasPrimary = !!primaryCc && !!primaryPhone;
  const canCallPrimary = hasPrimary && !activeCall.callActive;
  const callPrimary = canCallPrimary
    ? () => callNumber({ countryCode: primaryCc, phoneNo: primaryPhone })
    : undefined;
  const callPrimaryTitle = activeCall.callActive
    ? "A call is already in progress — open the transcript view to see it"
    : hasPrimary
    ? "Call patient"
    : callingNumber === null
    ? "The number this appointment is booked for isn't available yet"
    : patient
    ? "Patient has no primary phone number on file"
    : undefined;

  const addressBookUI = patient ? (
    <AddressBookDialog
      open={addressBookOpen}
      patient={patient}
      appointment={appointment}
      onClose={() => setAddressBookOpen(false)}
      disableCallButtons={activeCall.callActive}
      onCall={(target) => {
        setAddressBookOpen(false);
        callNumber(target);
      }}
    />
  ) : null;

  const dialogsUI = (
    <>
      {confirmDialog && (
        <CallInitiatedDialog
          callingFrom={confirmDialog.callingFrom}
          onClose={() => setConfirmDialog(null)}
        />
      )}
      {errorDialog && (
        <CallErrorDialog
          title={errorDialog.title}
          message={errorDialog.message}
          onClose={() => setErrorDialog(null)}
        />
      )}
    </>
  );

  return {
    /** True while a global call is ringing or connected. Reads through
     *  to ActiveCallProvider, so every screen sees the same value. */
    transcriptOpen: activeCall.modalVisible,
    callActive: activeCall.callActive,
    callNumber,
    callPrimary,
    canCallPrimary,
    callPrimaryTitle,
    openAddressBook,
    openCall,
    addressBookUI,
    /* transcriptUI is intentionally absent — the modal is mounted by
       ActiveCallProvider at app root. */
    dialogsUI,
  };
};

export default useCallFlow;
